import { Column, Entity, JoinColumn, ManyToOne, PrimaryGeneratedColumn, Repository, Index, ManyToMany, JoinTable } from 'typeorm';
import { ConnectorConfig, DataSourceConnectorType, DataSourceContext, ItemDef, QueryParams, SortDef } from './models';
import { ConnectorConfigCSV } from './connectors/csv/models';
import { Business, PublicBusiness } from '../business/business.entity';
import { ConnectorConfigSmartSheet } from './connectors/smartsheet/models';
import { PublicSite, Site } from '../site/site.entity';
import { FilterDef } from './models/filter.model';
import { ConnectorConfigNative } from './connectors/native/models';
import { DataStore } from './data-store/data-store.entity';
import { Crypt } from '../_core/crypt';
import { ConnectorConfigCloudinary } from './connectors/cloudinary/models';
import { ConnectorConfigPexels } from './connectors/pexels/models';
import { ConnectorConfigBria } from './connectors/bria/models';
import { ConnectorConfigBrandshare } from './connectors/brandshare/models';
import { ConnectorConfigBox } from './connectors/box/models';
import { ConnectorConfigStrapi } from "./connectors/strapi/models";
import { IsOptional } from "class-validator";
import { LocaleDto } from "../creative-unit-package/models/locale.dto";
import { ConnectorConfigAssetCollection } from './connectors/asset-collection/models';

export type PublicDataSource = Pick<
	DataSource,
	'id' | 'name' | 'slug' | 'connectorType' | 'dataSourceLink' | 'siteId' | 'locale' | 'created' | 'modified' | 'public'
> & {
	site?: PublicSite;
	businesses?: PublicBusiness[] | Business[] | Array<Partial<Business>>;
	config?: {
		target: string;
		context?: DataSourceContext[];
		mapping?: ItemDef[];
		params?: QueryParams;
		columnSeparator?: string;
		environment?: string;
		sortableFields?: SortDef[];
		filters?: FilterDef[];
		previewPath?: string;
		assetPathProperty?: string; // Points to where the asset path should be in the item result.
		assetTitleProperty?: string; // Points to where the asset name should be in the item result.
		tagPath?: string;
		accessToken?: string;
		attribution?: string;
		saveToLibrary?: boolean;
		[key: string]: any;
	};
};

@Entity('dataSources')
@Index(['slug', 'siteId'], { unique: true })
export class DataSource {
	public dataStoreRepository?: Repository<DataStore>;

	constructor(value?: Partial<DataSource>, dataStoreRepository?: Repository<DataStore>) {
		// Copy properties from value
		for (const k in value) {
			try {
				const val = value[k];
				if (val !== null && val !== undefined) {
					this[k] = val;
				}
			} catch (error) {
				console.error(`Error assigning property ${k}:`, error, 'Value type:', typeof value[k]);
				// Continue with other properties even if one fails
			}
		}
		this.dataStoreRepository = dataStoreRepository;

		// Safely handle config access with error handling
		try {
			if (this.config && !this.config.target?.length && this.connectorType === DataSourceConnectorType.Native) {
				// Create a new config object to avoid modifying the original
				const updatedConfig = { ...this.config, target: this.id };
				this.config = updatedConfig;
			}
		} catch (error) {
			console.error('Error accessing config in DataSource constructor:', error);
			// If config access fails, just continue without setting the target
		}
	}

	@PrimaryGeneratedColumn('uuid')
	id: string;

	@Column('text', { nullable: false })
	name: string;

	@Column({ type: 'text', nullable: false })
	slug: string;

	@Column({ type: 'enum', enum: DataSourceConnectorType })
	connectorType: DataSourceConnectorType;

	@Column('text', { nullable: true })
	dataSourceLink: string;

	@Column('uuid', { nullable: false })
	siteId: string;
	@ManyToOne(() => Site, {
		onDelete: 'CASCADE',
		nullable: false
	})
	@JoinColumn({ name: 'siteId' })
	site: Site;

	@ManyToMany(() => Business, {
		nullable: true
	})
	@JoinTable({
		name: 'dataSourceBusinesses',
		joinColumn: {
			name: 'dataSourcesId',
			referencedColumnName: 'id'
		},
		inverseJoinColumn: {
			name: 'businessesId',
			referencedColumnName: 'id'
		}
	})
	businesses?: Business[] | Array<Partial<Business>>;

	@Column('text', {
		name: 'config',
		nullable: true
	})
	_config: string;
	public get config():
		| ConnectorConfig
		| ConnectorConfigNative
		| ConnectorConfigCSV
		| ConnectorConfigSmartSheet
		| ConnectorConfigCloudinary
		| ConnectorConfigPexels
		| ConnectorConfigBria
		| ConnectorConfigAssetCollection {
		// TOOD: Create a migration to encrypt/decrypt based on siteId.
		// All existing data sources should be decrypted and re-encrypted with the siteId.
		const decrypted: ConnectorConfig = Crypt.decryptObject(this._config, undefined);
		// Target to this source's id if empty it's a native source.
		if (decrypted && !decrypted.target?.length && this.connectorType === DataSourceConnectorType.Native) {
			decrypted.target = this.id;
		}
		return decrypted;
	}
	public set config(
		value:
			| ConnectorConfig
			| ConnectorConfigNative
			| ConnectorConfigCSV
			| ConnectorConfigSmartSheet
			| ConnectorConfigCloudinary
			| ConnectorConfigPexels
			| ConnectorConfigBria
	) {
		// TOOD: Create a migration to encrypt/decrypt based on siteId.
		// All existing data sources should be decrypted and re-encrypted with the siteId.
		this._config = Crypt.encryptObject(value, undefined);
	}

	@IsOptional()
	@Column('jsonb', { nullable: true })
	locale?: LocaleDto;

	@Column('timestamptz', { nullable: true, default: () => 'NOW()' })
	created?: string;

	@Column('timestamptz', { nullable: true, default: () => 'NOW()' })
	modified?: string;

	@Column('boolean', { default: false })
	public: boolean = false;

	public toPublic(skipMask: boolean = false) {
		const pub: Partial<PublicDataSource> = {
			id: this.id,
			name: this.name,
			slug: this.slug,
			dataSourceLink: this.dataSourceLink,
			connectorType: this.connectorType,
			siteId: this.siteId,
			businesses: this.businesses,
			locale: this.locale,
			created: this.created,
			modified: this.modified,
			public: this.public
		};

		let connectorTypeConfig = getDataSourceConnectorType(this.connectorType);

		if (this.site) {
			pub.site = new Site(this.site).toPublic();
		}

		if (this.businesses?.length) {
			pub.businesses = this.businesses.map(b => {
				return new Business(b).toPublic();
			});
		}

		// The config should specifically have keys blacklisted so
		// credentials are not leaked.
		const protectedFields = ['accessToken'];
		if (this.config) {
			let conf = this.config;

			pub.config = {
				target: conf.target
			};

			if (conf.context) {
				pub.config.context = conf.context;
			}

			if (conf.mapping) {
				pub.config.mapping = conf.mapping;
			}

			if (conf.params) {
				pub.config.params = conf.params;
			}

			if (conf.sortableFields) {
				pub.config.sortableFields = conf.sortableFields;
			}

			if (conf.filters) {
				pub.config.filters = conf.filters || connectorTypeConfig?.filters;
			}

			if (conf.previewPath) {
				pub.config.previewPath = conf.previewPath;
			}

			if (conf.assetPathProperty) {
				pub.config.assetPathProperty = conf.assetPathProperty;
			}

			if (conf.assetTitleProperty) {
				pub.config.assetTitleProperty = conf.assetTitleProperty;
			}

			if (conf.tagPath) {
				pub.config.tagPath = conf.tagPath;
			}

			if (!conf.attribution) {
				pub.config.attribution = connectorTypeConfig?.attribution;
			}

			if (conf['columnSeparator']) {
				pub.config.columnSeparator = conf['columnSeparator'];
			}
			if (conf['environment']) {
				pub.config.environment = conf['environment'];
			}

			if (conf['saveToLibrary'] !== undefined) {
				pub.config.saveToLibrary = conf['saveToLibrary'];
			}

			// Remove protected fields from the config.
			if (!skipMask) {
				for (const field of protectedFields) {
					if (pub.config[field]) {
						// Masked to show the front end a value exists.
						pub.config[field] = '**************';
					}
				}
			}
		}

		return pub as PublicDataSource;
	}

	public toExport(): Omit<PublicDataSource, 'id' | 'siteId'> {
		const item = this.toPublic(true);
		item.id = undefined;
		item.siteId = undefined;

		return item;
	}
}

export function getDataSourceConnectorType(value: DataSourceConnectorType): ConnectorConfig {
	switch (value) {
		case DataSourceConnectorType.CSV:
			return new ConnectorConfigCSV();
		case DataSourceConnectorType.SmartSheet:
			return new ConnectorConfigSmartSheet();
		case DataSourceConnectorType.Cloudinary:
			return new ConnectorConfigCloudinary();
		case DataSourceConnectorType.Native:
			return new ConnectorConfigNative();
		case DataSourceConnectorType.Pexels:
			return new ConnectorConfigPexels();
		case DataSourceConnectorType.Strapi:
			return new ConnectorConfigStrapi();
		case DataSourceConnectorType.Bria:
			return new ConnectorConfigBria();
		case DataSourceConnectorType.Brandshare:
			return new ConnectorConfigBrandshare();
		case DataSourceConnectorType.Box:
			return new ConnectorConfigBox();
		case DataSourceConnectorType.AssetCollection:
			return new ConnectorConfigAssetCollection();
	}
}
